// client/src/components/admin/SuperAdminTaxTab.jsx
//
// Platform Admin → Tax. Issuing tax documents (final runs and corrections,
// which send documents to donors and raffle winners) is super-admin only for
// now. Per tenant: draft / issued counts, missing-data warnings, document
// review, "Generate draft", "Issue final", and corrections.
//
// API: /api/super-admin/tax (server/routes/superAdminTaxRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import {
  platformTaxTenants, platformTaxTenant, platformStartRun, platformCorrect, platformDocumentUrl, errorText,
} from '../../services/taxApi';
import { RefreshButton } from '../common/IconButton';

const SEV = { blocker: 'active', warning: 'warn', info: 'disabled' };

function TenantDetail({ tenantId, year, onChanged, notify }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    platformTaxTenant(tenantId, year).then(setD).catch((e) => setError(errorText(e, 'Failed to load tenant.')));
  }, [tenantId, year]);

  useEffect(() => { load(); }, [load]);

  const correct = async (doc) => {
    if (!window.confirm(`Issue a corrected version of "${doc.title}"${doc.recipientEmail ? ` for ${doc.recipientEmail}` : ''}? The original stays on record.`)) return;
    try { await platformCorrect(doc.id); notify('Correction queued.'); load(); onChanged(); } catch (e) { notify(errorText(e, 'Could not queue the correction.')); }
  };

  if (error) return <p style={{ color: '#a11' }}>{error}</p>;
  if (!d) return <p className="sa-loading">Loading…</p>;
  return (
    <div style={{ padding: '8px 0' }}>
      <h4 style={{ margin: '4px 0' }}>Missing information</h4>
      {d.warnings.length === 0 ? <p>None.</p> : (
        <ul style={{ margin: '4px 0 12px', paddingLeft: 18 }}>
          {d.warnings.map((w) => (
            <li key={w.code}><span className={`sa-badge ${SEV[w.severity] || ''}`}>{w.severity}</span> {w.message}{w.count ? ` (${w.count})` : ''}</li>
          ))}
        </ul>
      )}
      <h4 style={{ margin: '4px 0' }}>Documents</h4>
      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead><tr><th>Document</th><th>Recipient</th><th>Status</th><th /></tr></thead>
          <tbody>
            {d.documents.map((doc) => (
              <tr key={doc.id}>
                <td>{doc.title}{doc.containsTin ? ' · contains TINs' : ''}</td>
                <td>{doc.recipientEmail || 'Organization'}</td>
                <td><span className={`sa-badge ${doc.status === 'draft' ? 'warn' : 'enabled'}`}>{doc.status}{doc.version > 1 ? ` v${doc.version}` : ''}</span></td>
                <td className="sa-btn-row">
                  {/\.pdf$/i.test(doc.fileName) && <a className="sa-btn" href={platformDocumentUrl(doc.id, true)} target="_blank" rel="noopener noreferrer">View</a>}
                  <a className="sa-btn" href={platformDocumentUrl(doc.id)}>Download</a>
                  {doc.status !== 'draft' && <button type="button" className="sa-btn" onClick={() => correct(doc)}>Correct</button>}
                </td>
              </tr>
            ))}
            {d.documents.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center' }}>No documents yet — generate a draft.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SuperAdminTaxTab() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear - 1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setError('');
    platformTaxTenants(year).then(setData).catch((e) => setError(errorText(e, 'Failed to load tax status.')));
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const run = async (t, mode) => {
    if (mode === 'final' && !window.confirm(`Issue final ${year} tax documents for ${t.name}? Donors and winners are emailed when they are ready. Issued documents are never changed — mistakes are fixed with corrections.`)) return;
    setBusy(`${t.id}:${mode}`);
    setMsg('');
    try {
      await platformStartRun(t.id, year, mode);
      setMsg(`${mode === 'final' ? 'Final issue' : 'Draft'} queued for ${t.name}.`);
      load();
    } catch (e) {
      setMsg(errorText(e, 'Could not start the run.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="sa-section-header">
        <h3>Tax documents</h3>
        <div className="sa-btn-row">
          <select className="sa-tenant-select" value={year} onChange={(e) => { setOpen(null); setYear(Number(e.target.value)); }} aria-label="Tax year">
            {[thisYear, thisYear - 1, thisYear - 2, thisYear - 3].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <RefreshButton onClick={load} />
        </div>
      </div>
      <p>Only super admins can issue final documents or corrections. Tenant admins can generate and review drafts. Nothing is sent automatically.</p>
      {data && !data.rulesReady && <p><span className="sa-badge active">blocker</span> No tax rules for {year} in t_tax_rule.</p>}
      {data && data.unverifiedRules.length > 0 && <p><span className="sa-badge warn">warning</span> {year} thresholds not yet verified: {data.unverifiedRules.join(', ')}.</p>}
      {msg && <p role="status"><strong>{msg}</strong></p>}
      {error && <p style={{ color: '#a11' }}>{error}</p>}
      {!data && !error && <p className="sa-loading">Loading…</p>}
      {data && (
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr><th>Tenant</th><th>Exempt status</th><th>Drafts</th><th>Issued</th><th>Not yet sent</th><th>Last run</th><th /></tr>
            </thead>
            <tbody>
              {data.tenants.map((t) => (
                <React.Fragment key={t.id}>
                  <tr>
                    <td>{t.name} <span className="sa-mono">#{t.id}</span></td>
                    <td>{t.exemptStatus}</td>
                    <td>{t.drafts}</td>
                    <td>{t.issued}</td>
                    <td>{t.undelivered}</td>
                    <td>{t.lastRun ? <span className={`sa-badge ${t.lastRun.status === 'failed' ? 'active' : t.lastRun.status === 'done' ? 'enabled' : 'warn'}`}>{t.lastRun.mode} · {t.lastRun.status}</span> : '—'}</td>
                    <td className="sa-btn-row">
                      <button type="button" className="sa-btn" onClick={() => setOpen(open === t.id ? null : t.id)}>{open === t.id ? 'Hide' : 'Review'}</button>
                      <button type="button" className="sa-btn" disabled={!!busy} onClick={() => run(t, 'draft')}>Generate draft</button>
                      <button type="button" className="sa-btn primary" disabled={!!busy || year >= thisYear} onClick={() => run(t, 'final')}
                        title={year >= thisYear ? 'Final documents can be issued once the year has ended' : undefined}>Issue final</button>
                    </td>
                  </tr>
                  {open === t.id && (
                    <tr><td colSpan={7}><TenantDetail tenantId={t.id} year={year} onChanged={load} notify={setMsg} /></td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
